import { jsx as _jsx } from "react/jsx-runtime";
import { Spinner } from 'libs.nucleus';
// To be exposed to the API if needed
const OPTIONS = {
    cols: 'grid-cols-1 sm:grid-cols-6 md:grid-cols-12',
    gap: 'gap-2 sm:gap-4',
    bgColor: 'bg-neutral-100',
};
const GRID_ROW_HEIGHT_MAP = {
    xs: 'min-h-28',
    sm: 'min-h-48',
    base: 'min-h-80',
    lg: 'min-h-[32rem]',
    full: '',
};
const COL_SPAN_MAP = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
};
const ROW_SPAN_MAP = {
    1: 'row-span-1',
    2: 'row-span-2',
    3: 'row-span-3',
    4: 'row-span-4',
    5: 'row-span-5',
    6: 'row-span-6',
    7: 'row-span-7',
    8: 'row-span-8',
    9: 'row-span-9',
    10: 'row-span-10',
    11: 'row-span-11',
    12: 'row-span-12',
};
// Shared utility to generate responsive classes from a responsive object
const getResponsiveClasses = (span, spanMap, defaultBase) => {
    const classes = [];
    // Base (mobile): use default base value
    classes.push(spanMap[defaultBase]);
    // sm breakpoint
    if (span.sm !== undefined) {
        classes.push(`sm:${spanMap[span.sm]}`);
    }
    // md breakpoint
    if (span.md !== undefined) {
        classes.push(`md:${spanMap[span.md]}`);
    }
    // lg breakpoint
    if (span.lg !== undefined) {
        classes.push(`lg:${spanMap[span.lg]}`);
    }
    return classes.join(' ');
};
// Utility function to generate responsive column span classes
const getResponsiveColSpan = (span, defaultSpan) => {
    if (!span) {
        // When span is undefined, use default but make it responsive
        // Mobile: full width (1), sm: scale proportionally, md+: use default
        const mobileSpan = 1;
        const smSpan = Math.min(6, Math.ceil((defaultSpan / 12) * 6));
        const mdSpan = defaultSpan;
        const classes = [
            COL_SPAN_MAP[mobileSpan],
            smSpan > 0 ? `sm:${COL_SPAN_MAP[smSpan]}` : '',
            mdSpan > 0 ? `md:${COL_SPAN_MAP[mdSpan]}` : '',
        ].filter(Boolean);
        return classes.join(' ');
    }
    if (typeof span === 'number') {
        // Backward compatibility: single number value
        // On mobile (1 col), use full width; on sm (6 cols), scale proportionally; on md+ (12 cols), use original
        const mobileSpan = 1; // Always full width on mobile
        const smSpan = Math.min(6, Math.ceil((span / 12) * 6)); // Scale to 6-column grid
        const mdSpan = span; // Original value on 12-column grid
        const classes = [
            COL_SPAN_MAP[mobileSpan],
            smSpan > 0 ? `sm:${COL_SPAN_MAP[smSpan]}` : '',
            mdSpan > 0 ? `md:${COL_SPAN_MAP[mdSpan]}` : '',
        ].filter(Boolean);
        return classes.join(' ');
    }
    // Responsive object
    return getResponsiveClasses(span, COL_SPAN_MAP, 1);
};
// Utility function to generate responsive row span classes
const getResponsiveRowSpan = (span, defaultSpan) => {
    if (!span) {
        return ROW_SPAN_MAP[defaultSpan] || '';
    }
    if (typeof span === 'number') {
        // Backward compatibility: single number value
        // Keep same row span across all breakpoints for simplicity
        return ROW_SPAN_MAP[span] || '';
    }
    // Responsive object
    return getResponsiveClasses(span, ROW_SPAN_MAP, 1);
};
const PADDING_MAP = {
    0: 'p-0',
    4: 'p-4',
    8: 'p-8',
    16: 'p-16',
};
const BG_COLOR_MAP = {
    'white': 'bg-white',
    'x-light': 'bg-neutral-50',
    'light': 'bg-neutral-100',
    'medium': 'bg-neutral-200',
};
export const ReportingGrid = ({ cards, padding = 4, bgColor = 'light', rowHeight = 'base', isLoading = false, }) => {
    return (_jsx("div", { className: `grid ${OPTIONS.gap} ${OPTIONS.cols} ${BG_COLOR_MAP[bgColor]} ${PADDING_MAP[padding]}`, children: isLoading ? (_jsx("div", { className: 'flex justify-center col-span-12 py-24', children: _jsx(Spinner, {}) })) : (cards?.map(({ card, xSpan = 12, ySpan = 1 }, index) => {
            const colSpanClasses = getResponsiveColSpan(xSpan, 12);
            const rowSpanClasses = getResponsiveRowSpan(ySpan, 1);
            return (_jsx("div", { className: `flex ${rowSpanClasses} ${colSpanClasses} ${GRID_ROW_HEIGHT_MAP[rowHeight]}`, children: card }, `cell-${index}`));
        })) }));
};
